import type { User } from '@/types/auth';

export const RECIPE_INGREDIENT_MANAGER_ROLE = 'recipe_ingredient_manager';

const FULL_ACCESS_ROLES = new Set(['nutritionist']);
const ADMIN_ROLES = new Set(['admin', 'super_admin']);
const RECIPE_INGREDIENT_ROLES = new Set([RECIPE_INGREDIENT_MANAGER_ROLE]);
const RECIPE_INGREDIENT_PATH_PREFIXES = ['/recipes', '/ingredients'];
const INGREDIENT_PATH_PREFIXES = ['/ingredients'];
const ADMIN_PATH_PREFIXES = ['/admin'];
const NUTRITIONIST_ONLY_PATH_PREFIXES = ['/admin/client-invites'];
const AI_RECIPE_REVIEW_PATH = '/admin/ai-recipes';

export const normalizeUserRoles = (user: Pick<User, 'role' | 'roles'> | null | undefined): string[] => {
  const roles = Array.isArray(user?.roles)
    ? user.roles.map((role) => String(role || '').trim()).filter(Boolean)
    : [];

  if (roles.length > 0) {
    return Array.from(new Set(roles));
  }

  const fallbackRole = String(user?.role || '').trim();
  return fallbackRole ? [fallbackRole] : [];
};

export const hasFullAppAccess = (user: Pick<User, 'role' | 'roles'> | null | undefined): boolean =>
  normalizeUserRoles(user).some((role) => FULL_ACCESS_ROLES.has(role));

export const hasAdminAccess = (user: Pick<User, 'role' | 'roles'> | null | undefined): boolean =>
  normalizeUserRoles(user).some((role) => ADMIN_ROLES.has(role));

export const hasRecipeIngredientAccess = (user: Pick<User, 'role' | 'roles'> | null | undefined): boolean => {
  const roles = normalizeUserRoles(user);
  return roles.some((role) => FULL_ACCESS_ROLES.has(role) || RECIPE_INGREDIENT_ROLES.has(role));
};

export const hasRecipeManagerAccess = (user: Pick<User, 'role' | 'roles'> | null | undefined): boolean =>
  normalizeUserRoles(user).includes(RECIPE_INGREDIENT_MANAGER_ROLE);

export const hasIngredientCatalogAccess = (user: Pick<User, 'role' | 'roles'> | null | undefined): boolean => {
  const roles = normalizeUserRoles(user);
  return roles.some((role) => ADMIN_ROLES.has(role) || RECIPE_INGREDIENT_ROLES.has(role));
};

export const getDefaultAuthenticatedRoute = (user: Pick<User, 'role' | 'roles'> | null | undefined): string =>
  hasFullAppAccess(user) ? '/dashboard' : hasAdminAccess(user) ? '/admin/users' : '/recipes';

const AUTH_RETURN_PATH_PREFIXES = ['/login', '/signup', '/auth'];

/** Same-app relative path only; rejects auth pages and absolute/external URLs. */
export const normalizePostLoginReturnPath = (path: string | null | undefined): string | null => {
  if (!path || typeof path !== 'string') {
    return null;
  }

  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return null;
  }

  let pathname = trimmed;
  try {
    pathname = new URL(trimmed, 'https://caloriescience.local').pathname;
  } catch {
    return null;
  }

  if (
    AUTH_RETURN_PATH_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
  ) {
    return null;
  }

  return trimmed;
};

/** Prefer a safe deep-link `from` path; otherwise the role default authenticated route. */
export const resolvePostLoginRedirect = (
  from: string | null | undefined,
  user: Pick<User, 'role' | 'roles'> | null | undefined,
): string => normalizePostLoginReturnPath(from) || getDefaultAuthenticatedRoute(user);

export const isRecipeIngredientPath = (pathname: string): boolean =>
  RECIPE_INGREDIENT_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export const isIngredientCatalogPath = (pathname: string): boolean =>
  INGREDIENT_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export const isAdminPath = (pathname: string): boolean =>
  ADMIN_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export const isNutritionistOnlyPath = (pathname: string): boolean =>
  NUTRITIONIST_ONLY_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export const canAccessDashboardPath = (
  pathname: string,
  user: Pick<User, 'role' | 'roles'> | null | undefined
): boolean => {
  if (pathname === AI_RECIPE_REVIEW_PATH || pathname.startsWith(`${AI_RECIPE_REVIEW_PATH}/`)) {
    return hasRecipeManagerAccess(user);
  }

  if (isNutritionistOnlyPath(pathname)) {
    return hasFullAppAccess(user);
  }

  if (isAdminPath(pathname)) {
    return hasAdminAccess(user);
  }

  if (isIngredientCatalogPath(pathname)) {
    return hasIngredientCatalogAccess(user);
  }

  if (hasFullAppAccess(user)) {
    return true;
  }

  return hasRecipeIngredientAccess(user) && isRecipeIngredientPath(pathname);
};
